import {
  Button,
  column,
  Css,
  GridColumn,
  GridDataRow,
  GridTable,
  Icon,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Palette,
  simpleHeader,
  SimpleHeaderAndData,
  useModal,
} from "@homebound/beam";
import { Link } from "react-router-dom";
import { tagCell } from "src/components";
import { createCommitmentChangeOrderUrl, createCommitmentUrl } from "src/RouteUrls";
import { PurchaseOrderCleanup_CommitmentLikeFragment } from "./PurchaseOrderCleanupModal.generated";
import { reconcileAction } from "./ReconcileImpactedCommitmentsStep";

type PurchaseOrderCleanupModalProps = {
  projectId: string;
  onSaveDraftAndExit: () => Promise<void>;
  /** Only the commitments that actually need cleanup. */
  commitments: PurchaseOrderCleanup_CommitmentLikeFragment[];
};

export function PurchaseOrderCleanupModal(props: PurchaseOrderCleanupModalProps) {
  const { projectId, commitments, onSaveDraftAndExit } = props;
  const { closeModal } = useModal();

  return (
    <>
      <ModalHeader>Purchase Order Cleanup Required</ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.gap3.$}>
          <div css={Css.sm.gray900.$}>
            Some purchase orders affected by this Change Event require manual updates before it can be submitted for
            approval. After completing these actions, return here and click 'Create' to submit this Change Event for
            approval.
          </div>
          <GridTable columns={createColumns(projectId)} rows={createRows(commitments)} />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button label="Cancel" variant="quaternary" onClick={() => closeModal()} />
        <Button
          label="Save Draft & Exit"
          onClick={async () => {
            closeModal();
            await onSaveDraftAndExit();
          }}
        />
      </ModalFooter>
    </>
  );
}

type Row = SimpleHeaderAndData<PurchaseOrderCleanup_CommitmentLikeFragment>;

function createRows(commitments: PurchaseOrderCleanup_CommitmentLikeFragment[]): GridDataRow<Row>[] {
  return [simpleHeader, ...commitments.map((c) => ({ kind: "data" as const, id: c.id, data: c }))];
}

// The status tag and the action a PO needs come from `reconcileAction`, so the modal and the Reconcile step agree.
function createColumns(projectId: string): GridColumn<Row>[] {
  return [
    column<Row>({
      header: "Purchase Order",
      // Opened in a new tab, so the user can act on the PO without losing their place in the wizard
      data: (c) => ({
        content: (
          <Link
            target="_blank"
            to={
              c.__typename === "Commitment"
                ? createCommitmentUrl(projectId, c.id)
                : createCommitmentChangeOrderUrl(projectId, c.id)
            }
          >
            {c.shortName}
          </Link>
        ),
        value: c.shortName,
      }),
    }),
    column<Row>({
      header: "Status",
      data: (c) => {
        const { status, statusTagType } = reconcileAction(c);
        return tagCell(statusTagType, status);
      },
      w: "160px",
    }),
    column<Row>({
      header: "Action Required",
      data: (c) => {
        const { action, description } = reconcileAction(c);
        return {
          content: (
            <div css={Css.df.aic.gap1.$}>
              <span>{action}</span>
              <Icon icon="infoCircle" inc={2} color={Palette.Gray600} tooltip={description} />
            </div>
          ),
          value: action,
        };
      },
      w: 2,
    }),
  ];
}
